import React from "react";
import { CheckCircleIcon, ClockIcon, ExclamationCircleIcon } from "@heroicons/react/24/outline";

/**
 * Purchase Action Edit Bar Component
 * Shows save status and unsaved changes indicator
 * 
 * @param {boolean} hasUnsavedChanges - Whether there are unsaved changes
 * @param {boolean} isSaving - Whether save is in progress
 * @param {boolean} lastSaved - Timestamp of last save (optional)
 * @param {string} error - Error message if save failed (optional)
 */
export const PurchaseActionEditBar = ({
  hasUnsavedChanges = false,
  isSaving = false,
  lastSaved = null,
  error = null,
}) => {
  const getStatusDisplay = () => {
    if (error) {
      return {
        icon: ExclamationCircleIcon,
        text: "Feil ved lagring",
        color: "text-red-600",
        bgColor: "bg-red-50",
      };
    }
    
    if (isSaving) {
      return {
        icon: ClockIcon,
        text: "Lagrer...",
        color: "text-blue-600",
        bgColor: "bg-blue-50",
      };
    }
    
    if (hasUnsavedChanges) {
      return {
        icon: ClockIcon,
        text: "Ulagrede endringer",
        color: "text-yellow-600",
        bgColor: "bg-yellow-50",
      };
    }
    
    if (lastSaved) {
      return {
        icon: CheckCircleIcon,
        text: `Lagret ${formatLastSaved(lastSaved)}`,
        color: "text-green-600",
        bgColor: "bg-green-50",
      };
    }
    
    return {
      icon: CheckCircleIcon,
      text: "Lagret",
      color: "text-green-600",
      bgColor: "bg-green-50",
    };
  };

  const formatLastSaved = (timestamp) => {
    if (!timestamp) return "";
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffSecs < 10) {
      return "nå";
    } else if (diffSecs < 60) {
      return `for ${diffSecs} sekunder siden`;
    } else if (diffMins < 60) {
      return `for ${diffMins} ${diffMins === 1 ? "minutt" : "minutter"} siden`;
    } else if (diffHours < 24) {
      return `for ${diffHours} ${diffHours === 1 ? "time" : "timer"} siden`;
    } else {
      return date.toLocaleDateString("nb-NO", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  };

  const status = getStatusDisplay();
  const Icon = status.icon;

  return (
    <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-200">
      <div className="flex items-center space-x-2">
        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${status.color} ${status.bgColor}`}>
          <Icon className={`h-4 w-4 mr-2 ${isSaving ? "animate-spin" : ""}`} />
          <span>{status.text}</span>
        </div>
        {error && (
          <span className="text-sm text-red-600">{error}</span>
        )}
      </div>
    </div>
  );
};





